/* =============================================================================
 * Prostay App — Icon system
 * -----------------------------------------------------------------------------
 * A thin, centralized wrapper over lucide-react (already a dependency). All
 * product UI imports icons FROM HERE, never directly from 'lucide-react', so
 * we have one place to enforce design-system defaults and to swap the
 * underlying set later without touching call sites.
 *
 * Design-system icon rules (foundations.md §5):
 *   - color: always currentColor (inherits from surrounding text)
 *   - stroke width: 1.75 (consistent, never per-icon)
 *   - size presets: inline 16, nav 20, badge 14
 *
 * USAGE:
 *   import { Icon, ICON_SIZE } from 'ui/icons';
 *   <Icon.Search />                       // 16px inline
 *   <Icon.LayoutDashboard size={ICON_SIZE.nav} />
 *   <Icon.Check className="..." />
 * ========================================================================== */

import React from 'react';
import {
  // navigation / shell
  LayoutDashboard,
  CalendarDays,
  MessagesSquare,
  ClipboardList,
  Users,
  BedDouble,
  Tags,
  BarChart3,
  Settings,
  LayoutGrid,
  PanelLeftClose,
  PanelLeft,
  Building2,
  // actions
  Plus,
  Paperclip,
  Minus,
  Search,
  Filter,
  Bookmark,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  ChevronsUpDown,
  MoreHorizontal,
  MoreVertical,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Download,
  Upload,
  RefreshCw,
  Pencil,
  Trash2,
  Eye,
  ExternalLink,
  Copy,
  // status / feedback
  CircleCheck,
  CircleAlert,
  TriangleAlert,
  Info,
  Bell,
  Clock,
  Loader2,
  // domain
  CreditCard,
  Receipt,
  Wallet,
  DollarSign,
  TrendingUp,
  TrendingDown,
  User,
  UserCircle,
  LogOut,
  HelpCircle,
  Mail,
  Phone,
  MapPin,
  Star,
  Sparkles,
  Briefcase,
  BookOpen,
  Network,
  Globe,
  ShoppingCart,
  Anchor,
  Plug,
  Activity,
  GripVertical,
  Columns3,
  SlidersHorizontal,
  ListFilter,
  // menu items + modules
  Calculator,
  SprayCan,
  Megaphone,
  Compass,
  Waypoints,
  CalendarRange,
  ConciergeBell,
  // per-module menu items
  CalendarX,
  Layers,
  Gauge,
  AppWindow,
  Palette,
  FileText,
  Package,
  Printer,
  ArrowDownCircle,
  ArrowUpCircle,
  ShieldCheck,
  Bot,
  Folder,
  Folders,
  Signature,
  Zap,
  ListChecks,
  ArrowLeftRight,
  Boxes,
  ShoppingBag,
  UserCog,
  Percent,
  Puzzle,
  Files,
  Coins,
  // calendar
  Lock,
  Link2,
  Ban,
  Wrench,
  Fingerprint,
  KeyRound,
  Delete,
} from 'lucide-react';

export const ICON_SIZE = {
  inline: 16,
  nav: 20,
  badge: 14,
  lg: 24,
};

const DEFAULTS = {
  size: ICON_SIZE.inline,
  strokeWidth: 1.75,
  // color intentionally omitted → Lucide defaults to currentColor
  'aria-hidden': true,
};

/**
 * Wrap a Lucide component so it carries our design-system defaults while still
 * allowing every prop (size, strokeWidth, color, className, onClick…) to be
 * overridden at the call site.
 */
const makeIcon = (LucideComponent, displayName) => {
  const Wrapped = React.forwardRef((props, ref) => (
    <LucideComponent ref={ref} {...DEFAULTS} {...props} />
  ));
  Wrapped.displayName = `Icon.${displayName}`;
  return Wrapped;
};

export const Icon = {
  // navigation / shell
  LayoutDashboard: makeIcon(LayoutDashboard, 'LayoutDashboard'),
  Calendar: makeIcon(CalendarDays, 'Calendar'),
  Messages: makeIcon(MessagesSquare, 'Messages'),
  ClipboardList: makeIcon(ClipboardList, 'ClipboardList'),
  Users: makeIcon(Users, 'Users'),
  Bed: makeIcon(BedDouble, 'Bed'),
  Tags: makeIcon(Tags, 'Tags'),
  BarChart: makeIcon(BarChart3, 'BarChart'),
  Settings: makeIcon(Settings, 'Settings'),
  LayoutGrid: makeIcon(LayoutGrid, 'LayoutGrid'),
  PanelLeftClose: makeIcon(PanelLeftClose, 'PanelLeftClose'),
  PanelLeft: makeIcon(PanelLeft, 'PanelLeft'),
  Building: makeIcon(Building2, 'Building'),
  // actions
  Plus: makeIcon(Plus, 'Plus'),
  Paperclip: makeIcon(Paperclip, 'Paperclip'),
  Minus: makeIcon(Minus, 'Minus'),
  Search: makeIcon(Search, 'Search'),
  Filter: makeIcon(Filter, 'Filter'),
  Bookmark: makeIcon(Bookmark, 'Bookmark'),
  Check: makeIcon(Check, 'Check'),
  X: makeIcon(X, 'X'),
  ChevronDown: makeIcon(ChevronDown, 'ChevronDown'),
  ChevronUp: makeIcon(ChevronUp, 'ChevronUp'),
  ChevronRight: makeIcon(ChevronRight, 'ChevronRight'),
  ChevronLeft: makeIcon(ChevronLeft, 'ChevronLeft'),
  ChevronsUpDown: makeIcon(ChevronsUpDown, 'ChevronsUpDown'),
  MoreHorizontal: makeIcon(MoreHorizontal, 'MoreHorizontal'),
  MoreVertical: makeIcon(MoreVertical, 'MoreVertical'),
  ArrowUp: makeIcon(ArrowUp, 'ArrowUp'),
  ArrowDown: makeIcon(ArrowDown, 'ArrowDown'),
  ArrowLeft: makeIcon(ArrowLeft, 'ArrowLeft'),
  ArrowRight: makeIcon(ArrowRight, 'ArrowRight'),
  ArrowUpRight: makeIcon(ArrowUpRight, 'ArrowUpRight'),
  Download: makeIcon(Download, 'Download'),
  Upload: makeIcon(Upload, 'Upload'),
  Refresh: makeIcon(RefreshCw, 'Refresh'),
  Pencil: makeIcon(Pencil, 'Pencil'),
  Trash: makeIcon(Trash2, 'Trash'),
  Eye: makeIcon(Eye, 'Eye'),
  ExternalLink: makeIcon(ExternalLink, 'ExternalLink'),
  Copy: makeIcon(Copy, 'Copy'),
  // status / feedback
  CircleCheck: makeIcon(CircleCheck, 'CircleCheck'),
  CircleAlert: makeIcon(CircleAlert, 'CircleAlert'),
  TriangleAlert: makeIcon(TriangleAlert, 'TriangleAlert'),
  Info: makeIcon(Info, 'Info'),
  Bell: makeIcon(Bell, 'Bell'),
  Clock: makeIcon(Clock, 'Clock'),
  Spinner: makeIcon(Loader2, 'Spinner'),
  // domain
  CreditCard: makeIcon(CreditCard, 'CreditCard'),
  Receipt: makeIcon(Receipt, 'Receipt'),
  Wallet: makeIcon(Wallet, 'Wallet'),
  DollarSign: makeIcon(DollarSign, 'DollarSign'),
  TrendingUp: makeIcon(TrendingUp, 'TrendingUp'),
  TrendingDown: makeIcon(TrendingDown, 'TrendingDown'),
  User: makeIcon(User, 'User'),
  UserCircle: makeIcon(UserCircle, 'UserCircle'),
  LogOut: makeIcon(LogOut, 'LogOut'),
  Help: makeIcon(HelpCircle, 'Help'),
  Mail: makeIcon(Mail, 'Mail'),
  Phone: makeIcon(Phone, 'Phone'),
  MapPin: makeIcon(MapPin, 'MapPin'),
  Star: makeIcon(Star, 'Star'),
  Sparkles: makeIcon(Sparkles, 'Sparkles'),
  Briefcase: makeIcon(Briefcase, 'Briefcase'),
  BookOpen: makeIcon(BookOpen, 'BookOpen'),
  Network: makeIcon(Network, 'Network'),
  Globe: makeIcon(Globe, 'Globe'),
  ShoppingCart: makeIcon(ShoppingCart, 'ShoppingCart'),
  Anchor: makeIcon(Anchor, 'Anchor'),
  Plug: makeIcon(Plug, 'Plug'),
  Activity: makeIcon(Activity, 'Activity'),
  Grip: makeIcon(GripVertical, 'Grip'),
  Columns: makeIcon(Columns3, 'Columns'),
  Sliders: makeIcon(SlidersHorizontal, 'Sliders'),
  ListFilter: makeIcon(ListFilter, 'ListFilter'),
  // menu items + modules
  Calculator: makeIcon(Calculator, 'Calculator'),
  SprayCan: makeIcon(SprayCan, 'SprayCan'),
  Megaphone: makeIcon(Megaphone, 'Megaphone'),
  Compass: makeIcon(Compass, 'Compass'),
  Waypoints: makeIcon(Waypoints, 'Waypoints'),
  CalendarRange: makeIcon(CalendarRange, 'CalendarRange'),
  ConciergeBell: makeIcon(ConciergeBell, 'ConciergeBell'),
  // per-module menu items
  CalendarX: makeIcon(CalendarX, 'CalendarX'),
  Layers: makeIcon(Layers, 'Layers'),
  Gauge: makeIcon(Gauge, 'Gauge'),
  AppWindow: makeIcon(AppWindow, 'AppWindow'),
  Palette: makeIcon(Palette, 'Palette'),
  FileText: makeIcon(FileText, 'FileText'),
  Package: makeIcon(Package, 'Package'),
  Printer: makeIcon(Printer, 'Printer'),
  ArrowDownCircle: makeIcon(ArrowDownCircle, 'ArrowDownCircle'),
  ArrowUpCircle: makeIcon(ArrowUpCircle, 'ArrowUpCircle'),
  ShieldCheck: makeIcon(ShieldCheck, 'ShieldCheck'),
  Bot: makeIcon(Bot, 'Bot'),
  Folder: makeIcon(Folder, 'Folder'),
  Folders: makeIcon(Folders, 'Folders'),
  Signature: makeIcon(Signature, 'Signature'),
  Zap: makeIcon(Zap, 'Zap'),
  ListChecks: makeIcon(ListChecks, 'ListChecks'),
  ArrowLeftRight: makeIcon(ArrowLeftRight, 'ArrowLeftRight'),
  Boxes: makeIcon(Boxes, 'Boxes'),
  ShoppingBag: makeIcon(ShoppingBag, 'ShoppingBag'),
  UserCog: makeIcon(UserCog, 'UserCog'),
  Percent: makeIcon(Percent, 'Percent'),
  Puzzle: makeIcon(Puzzle, 'Puzzle'),
  Files: makeIcon(Files, 'Files'),
  Coins: makeIcon(Coins, 'Coins'),
  // calendar
  Lock: makeIcon(Lock, 'Lock'),
  /** The backspace key glyph, for the PIN pad. */
  Delete: makeIcon(Delete, 'Delete'),
  Link: makeIcon(Link2, 'Link'),
  Ban: makeIcon(Ban, 'Ban'),
  Wrench: makeIcon(Wrench, 'Wrench'),
  Fingerprint: makeIcon(Fingerprint, 'Fingerprint'),
  KeyRound: makeIcon(KeyRound, 'KeyRound'),
};

/* =============================================================================
 * Semantic maps — sidebar navigation + product modules
 * -----------------------------------------------------------------------------
 * The canonical icon for each menu item and module, in display order. The
 * `key` matches the app's internal identifier where one exists, so the shell
 * can resolve an icon from a nav/module key without a per-call-site decision.
 *
 *   import { NAV_ICONS, MODULE_ICONS } from 'ui/icons';
 *   NAV_ICONS.find(i => i.key === 'dashboard').Icon
 * ========================================================================== */

export const NAV_ICONS = [
  { key: 'dashboard',      label: 'Dashboard',            Icon: Icon.LayoutDashboard },
  { key: 'communication',  label: 'Communication',        Icon: Icon.Messages },
  { key: 'calendar',       label: 'Calendar',             Icon: Icon.Calendar },
  { key: 'reservations',   label: 'Reservations',         Icon: Icon.ClipboardList },
  { key: 'guests',         label: 'Guests',               Icon: Icon.Users },
  { key: 'booking-sources',label: 'Booking Sources',      Icon: Icon.Megaphone },
  { key: 'housekeeping',   label: 'Housekeeping',         Icon: Icon.SprayCan },
  { key: 'rates',          label: 'Rates & Availability', Icon: Icon.Tags },
  { key: 'reports',        label: 'Reports',              Icon: Icon.BarChart },
  { key: 'settings',       label: 'Settings',             Icon: Icon.Settings },
];

export const MODULE_ICONS = [
  { key: 'PropertyManagementSystem', label: 'Property Management System', Icon: Icon.Building },
  { key: 'KnowledgeBase',            label: 'Knowledge base',             Icon: Icon.BookOpen },
  { key: 'ChannelManager',           label: 'Channel Manager',            Icon: Icon.Waypoints },
  { key: 'BookingEngine',            label: 'Booking Engine',             Icon: Icon.Globe },
  { key: 'PointofSale',              label: 'Point of Sale',              Icon: Icon.ShoppingCart },
  { key: 'ProstayPay',               label: 'Prostay Pay',                Icon: Icon.CreditCard },
  { key: 'Ocean',                    label: 'Prostay AI',                 Icon: Icon.Sparkles },
  { key: 'Accounting',               label: 'Prostay Accounting',         Icon: Icon.Calculator },
  { key: 'DiveCenter',               label: 'Dive Center Management',     Icon: Icon.Anchor },
  { key: 'RevenueManagement',        label: 'Revenue Management',         Icon: Icon.TrendingUp },
  { key: 'Integrations',             label: 'Integrations',               Icon: Icon.Plug },
  { key: 'BillingSubscriptionPortal',label: 'Billing & Subscription',     Icon: Icon.Receipt },
  { key: 'UserActivityLog',          label: 'User Activity Log',          Icon: Icon.Activity },
];

/** Resolve a nav icon component by key (falls back to a neutral dot icon). */
export const getNavIcon = (key) => (NAV_ICONS.find((n) => n.key === key) || {}).Icon || Icon.ClipboardList;
/** Resolve a module icon component by internal module key. */
export const getModuleIcon = (key) => (MODULE_ICONS.find((m) => m.key === key) || {}).Icon || Icon.LayoutGrid;

/* =============================================================================
 * Per-module menu icons
 * -----------------------------------------------------------------------------
 * Every product module has its own left-hand menu with a different set of
 * items. This is the canonical icon for each item, grouped by module key and
 * kept in the same order the menu renders. `label` matches the in-app i18n
 * string so a renderer can match by label when no stable key exists.
 *
 *   import { MODULE_MENU_ICONS } from 'ui/icons';
 *   MODULE_MENU_ICONS.ChannelManager.items  // → [{ label, Icon }, …]
 * ========================================================================== */

export const MODULE_MENU_ICONS = {
  // Property Management System — the main app nav (see NAV_ICONS for full list)
  PropertyManagementSystem: {
    label: 'Property Management System',
    items: NAV_ICONS.map(({ label, Icon: I }) => ({ label, Icon: I })),
  },

  ChannelManager: {
    label: 'Channel Manager',
    items: [
      { label: 'Channels',  Icon: Icon.Network },
      { label: 'Closeouts', Icon: Icon.CalendarX },
      { label: 'Allotment', Icon: Icon.Layers },
      { label: 'Currency',  Icon: Icon.Coins },
    ],
  },

  BookingEngine: {
    label: 'Booking Engine',
    items: [
      { label: 'Summary',         Icon: Icon.Gauge },
      { label: 'Website Widgets', Icon: Icon.AppWindow },
      { label: 'Currencies',      Icon: Icon.Coins },
      { label: 'Customization',   Icon: Icon.Palette },
      { label: 'Custom Pages',    Icon: Icon.FileText },
      { label: 'Analytics',       Icon: Icon.BarChart },
      { label: 'Settings',        Icon: Icon.Settings },
    ],
  },

  PointofSale: {
    label: 'Point of Sale',
    items: [
      { label: 'Dashboard', Icon: Icon.LayoutDashboard },
      { label: 'Products',  Icon: Icon.Package },
      { label: 'Floors',    Icon: Icon.LayoutGrid },
      { label: 'Printing',  Icon: Icon.Printer },
      { label: 'Payments',  Icon: Icon.CreditCard },
      { label: 'Settings',  Icon: Icon.Settings },
    ],
  },

  ProstayPay: {
    label: 'Prostay Pay',
    items: [
      { label: 'Dashboard',         Icon: Icon.LayoutDashboard },
      { label: 'Money In',          Icon: Icon.ArrowDownCircle },
      { label: 'Money Out',         Icon: Icon.ArrowUpCircle },
      { label: 'Risk & Compliance', Icon: Icon.ShieldCheck },
      { label: 'Settings',          Icon: Icon.Settings },
    ],
  },

  Ocean: {
    label: 'Prostay AI',
    items: [
      { label: 'General Settings',       Icon: Icon.Settings },
      { label: 'Prostay Integration',    Icon: Icon.Plug },
      { label: 'Email Account Settings', Icon: Icon.Mail },
      { label: 'Learning Material',      Icon: Icon.BookOpen },
      { label: 'Chat Settings',          Icon: Icon.Messages },
      { label: 'Behavior',               Icon: Icon.Bot },
    ],
  },

  KnowledgeBase: {
    label: 'Knowledge base',
    items: [
      { label: 'Knowledge-base', Icon: Icon.BookOpen },
      { label: 'Categories',     Icon: Icon.Folder },
      { label: 'Subcategories',  Icon: Icon.Folders },
    ],
  },

  Communication: {
    label: 'Communication',
    items: [
      { label: 'Dashboard',       Icon: Icon.LayoutDashboard },
      { label: 'Email Signature', Icon: Icon.Signature },
      { label: 'Knowledge-base',  Icon: Icon.BookOpen },
    ],
  },

  DiveCenter: {
    label: 'Dive Center',
    items: [
      { label: 'Dive Center Reservation', Icon: Icon.ClipboardList },
      { label: 'Calendar',                Icon: Icon.Calendar },
      { label: 'Settings',                Icon: Icon.Settings },
    ],
  },

  RevenueManagement: {
    label: 'Revenue Management',
    items: [
      { label: 'Dashboard',       Icon: Icon.LayoutDashboard },
      { label: 'Dynamic Pricing', Icon: Icon.DollarSign },
      { label: 'Rate Calendar',   Icon: Icon.CalendarRange },
      { label: 'Autopilot',       Icon: Icon.Zap },
      { label: 'Pricing Rules',   Icon: Icon.ListChecks },
      { label: 'Pickup & Pace',   Icon: Icon.BarChart },
      { label: 'Reports',         Icon: Icon.FileText },
    ],
  },

  Accounting: {
    label: 'Prostay Accounting',
    items: [
      { label: 'Dashboard',          Icon: Icon.LayoutDashboard },
      { label: 'Tasks',              Icon: Icon.ListChecks },
      { label: 'Transactions',       Icon: Icon.ArrowLeftRight },
      { label: 'Fixed Assets',       Icon: Icon.Boxes },
      { label: 'Sales',              Icon: Icon.ShoppingBag },
      { label: 'Expenses',           Icon: Icon.Wallet },
      { label: 'Employees',          Icon: Icon.UserCog },
      { label: 'Taxes',              Icon: Icon.Percent },
      { label: 'Account & Settings', Icon: Icon.Settings },
    ],
  },

  Integrations: {
    label: 'Integrations',
    items: [
      { label: 'Payment Options',     Icon: Icon.CreditCard },
      { label: 'Invoicing',           Icon: Icon.FileText },
      { label: 'Folio Settings',      Icon: Icon.Files },
      { label: 'Tax & Fees Settings', Icon: Icon.Percent },
      { label: 'Import Reservations', Icon: Icon.Download },
      { label: 'Add-on Settings',     Icon: Icon.Puzzle },
      { label: 'Currencies',          Icon: Icon.Coins },
      { label: 'Item Categories',     Icon: Icon.Folder },
      { label: 'Items and Services',  Icon: Icon.ConciergeBell },
    ],
  },

  BillingSubscriptionPortal: {
    label: 'Billing & Subscription',
    items: [
      { label: 'Billing Portal',       Icon: Icon.CreditCard },
      { label: 'Billing Profile',      Icon: Icon.UserCircle },
      { label: 'Billing Notification', Icon: Icon.Bell },
    ],
  },

  SystemSettings: {
    label: 'System Settings',
    items: [
      { label: 'System Settings',   Icon: Icon.Settings },
      { label: 'Notifications',     Icon: Icon.Bell },
      { label: 'Property Settings', Icon: Icon.Building },
      { label: 'Email Settings',    Icon: Icon.Mail },
      { label: 'Users',             Icon: Icon.Users },
    ],
  },

  AccountSettings: {
    label: 'Account & Settings',
    items: [
      { label: 'Company', Icon: Icon.Building },
      { label: 'Sale',    Icon: Icon.ShoppingBag },
      { label: 'Expense', Icon: Icon.Wallet },
      { label: 'Time',    Icon: Icon.Clock },
      { label: 'Advance', Icon: Icon.Sliders },
    ],
  },

  GeneralSettings: {
    label: 'General Settings',
    items: [
      { label: 'Guest Statuses',             Icon: Icon.Tags },
      { label: 'Guest custom fields',        Icon: Icon.Users },
      { label: 'Reservation custom fields',  Icon: Icon.ClipboardList },
    ],
  },
};

export default Icon;
